import { useGetCurrentUserQuery } from '@sellsen/services';
import { Alert, Button, Space } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsSeller } from '../../hooks/use-is-seller';
import {
  evaluateSellerLimitBanners,
  INACTIVE_CLEANUP_PATH,
  SELLER_LIMIT_TARGET,
  sellerLimitBannerDescription,
  sellerLimitBannerTitle,
  type SellerLimitKind,
} from './seller-limit-banner';

const DISMISS_KEY = 'sellsen.sellerLimitBanner.dismissed';

const readDismissed = (): Set<SellerLimitKind> => {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(
      Array.isArray(parsed)
        ? parsed.filter(
            (k): k is SellerLimitKind => k === 'catalog' || k === 'asinPool'
          )
        : []
    );
  } catch {
    return new Set();
  }
};

const writeDismissed = (kinds: Set<SellerLimitKind>) => {
  try {
    sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...kinds]));
  } catch {
    // depolama kapalıysa kapatma yalnız bu render ömründe geçerli kalır
  }
};

/**
 * Üst çubuğun altında, her sayfada duran kota şeridi. Yalnız satıcı rolünde ve kullanıcının kota
 * kademesi varsa görünür (`/api/users/me → sellerLimits`; admin'de kota yok, alan boş gelir).
 *
 * Sarı (%90–99) oturum boyunca kapatılabilir (sessionStorage); kırmızı (%100+) kapatılamaz ve
 * önceki kapatmayı geçersiz kılar. Havuz ekleme/silme ve envanter silme mutasyonları kullanıcı
 * sorgusunu yenilediği için şerit silme sonrası kendiliğinden güncellenir.
 */
export const SellerLimitBanner = () => {
  const navigate = useNavigate();
  const isSeller = useIsSeller();
  const { data: user } = useGetCurrentUserQuery();
  const [dismissed, setDismissed] = useState<Set<SellerLimitKind>>(readDismissed);

  const banners = useMemo(
    () => evaluateSellerLimitBanners(user?.sellerLimits, dismissed),
    [user?.sellerLimits, dismissed]
  );

  const dismiss = useCallback((kind: SellerLimitKind) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(kind);
      writeDismissed(next);
      return next;
    });
  }, []);

  if (!isSeller || banners.length === 0) {
    return null;
  }

  return (
    <Space direction="vertical" size={8} style={{ display: 'flex', marginBottom: 16 }}>
      {banners.map((b) => {
        const target = SELLER_LIMIT_TARGET[b.kind];
        const isError = b.severity === 'error';
        return (
          <Alert
            key={b.kind}
            type={isError ? 'error' : 'warning'}
            showIcon
            banner
            role={isError ? 'alert' : 'status'}
            closable={!isError}
            onClose={() => dismiss(b.kind)}
            message={<strong>{sellerLimitBannerTitle(b)}</strong>}
            description={sellerLimitBannerDescription(b, user?.sellerLimitTier)}
            action={
              <Space wrap>
                <Button
                  size="small"
                  type={isError ? 'primary' : 'default'}
                  onClick={() => navigate(target.path)}
                >
                  {target.label}
                </Button>
                {isError && b.kind === 'catalog' ? (
                  <Button size="small" onClick={() => navigate(INACTIVE_CLEANUP_PATH)}>
                    İnaktif ürünleri temizle
                  </Button>
                ) : null}
              </Space>
            }
            style={{ borderRadius: 6 }}
          />
        );
      })}
    </Space>
  );
};
